import React, { useEffect, useState } from "react";
import { themeStyle } from "../lib/themes";
import { Link, useParams } from "react-router-dom";
import { Heart, MapPin, Calendar, Camera, Mic, Square, Star } from "lucide-react";
import CountdownCard from "../components/CountdownCard";
import RegistrySection from "../components/RegistrySection";
import Seo, { buildBreadcrumbSchema } from "../components/Seo";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { VenueDetail, guestKeyFor } from "../components/VenueDetail";
import { HeroSlides, mediaSrc } from "../components/site/HeroSlides";
import { VideoBox } from "../components/site/VideoBox";
import { bannerText } from "../lib/bannerStyle";
import { GuestHub } from "../components/site/GuestHub";
import { LanguagePicker } from "../components/LanguagePicker";

const FALLBACK_HERO = "https://images.pexels.com/photos/33403878/pexels-photo-33403878.jpeg";

const LANGS = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
];

export default function PublicSite() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [err, setErr] = useState(null);
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return "en";
  });

  const [unlocked, setUnlocked] = useState(0);
  useEffect(() => {
    const g = guestKeyFor(slug);
    if (lang && lang !== "en") {
      api.get(`/public/site/${slug}/translated?lang=${lang}&guest=${g}`)
        .then((r) => setW(r.data?.wedding))
        .catch((e) => setErr(e?.response?.status === 404 ? "Wedding site not found." : "Couldn't load."));
    } else {
      api.get(`/public/weddings/${slug}?guest=${g}`)
        .then((r) => setW(r.data))
        .catch((e) => setErr(e?.response?.status === 404 ? "Wedding site not found." : "Couldn't load."));
    }
  }, [slug, lang, unlocked]);

  if (err) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-center"><Heart className="w-8 h-8 text-[var(--gold)] mx-auto mb-4" /><p className="font-serif text-3xl">{err}</p><Link to="/" className="btn-gold mt-6">Back home</Link></div></div>;
  }
  if (!w) return null;

  const cover = mediaSrc(w.cover_image) || FALLBACK_HERO;
  const dateStr = w.wedding_date ? new Date(w.wedding_date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";

  const coupleNames =
    w.bride_name && w.groom_name ? `${w.bride_name} & ${w.groom_name}` : (w.title || "Our Wedding");
  const eventSchema = w.wedding_date
    ? {
        "@context": "https://schema.org",
        "@type": "Event",
        name: `${coupleNames} Wedding`,
        startDate: w.wedding_date,
        eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        location: w.venue
          ? { "@type": "Place", name: w.venue, address: w.venue_address || w.venue }
          : undefined,
        image: cover,
        organizer: { "@type": "Organization", name: "LockedInForever", url: "https://lockedinforever.com" },
      }
    : null;

  return (
    <div data-testid="public-site" className="bg-[var(--bg)]" style={themeStyle(w.template)}>
      <Seo
        title={`${coupleNames}${dateStr ? " — " + dateStr : ""}`}
        description={`Join ${coupleNames} for their wedding. RSVP, share photos, and celebrate the day on LockedInForever.`}
        path={`/site/${slug}`}
        image={cover}
        type="article"
        schema={[
          eventSchema,
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: coupleNames, url: `/site/${slug}` },
          ]),
        ].filter(Boolean)}
      />
      {w.is_demo && (
        <div data-testid="demo-badge" className="sticky top-0 z-40 bg-[var(--gold)] text-white text-[12px] py-2 px-4 flex items-center justify-between gap-3">
          <Link to="/" data-testid="demo-back-link" className="font-semibold uppercase tracking-[0.16em] whitespace-nowrap">← Back to LockedInForever</Link>
          <span className="hidden sm:inline">Demo wedding · explore freely, nothing you do here is saved</span>
          <Link to="/signup" className="underline font-semibold whitespace-nowrap" data-testid="demo-create-link">Create yours</Link>
        </div>
      )}

      <LanguagePicker className={`absolute ${w.is_demo ? "top-14" : "top-4"} right-4`} />
      {w.top_story && (
        <section className="bg-[#0b0b0b] text-white px-6 pt-20 pb-10" data-testid="top-story">
          <div className="max-w-5xl mx-auto grid md:grid-cols-[minmax(0,420px)_1fr] gap-8 items-center">
            <img src={`${BACKEND_URL}${w.top_story.card_url}`} alt={w.top_story.headline} className="w-full aspect-square object-cover rounded-2xl" data-testid="top-story-card" />
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-[#d41a24] font-bold">Top story</p>
              <p className="font-serif text-3xl md:text-4xl font-light mt-3 leading-tight" data-testid="top-story-headline">{w.top_story.headline}</p>
              {w.top_story.by && <p className="text-white/60 text-sm mt-3">Photo by {w.top_story.by}</p>}
            </div>
          </div>
        </section>
      )}
      {/* Hero */}
      <section className="relative h-[85vh] min-h-[600px]">
        <HeroSlides slides={w.banner_slides} cover={cover} coverFocus={w.cover_focus} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/40" />
        <div className="relative h-full flex flex-col items-center justify-center text-center text-white px-6">
          {w.hero_featured_at && (
            <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-[#C8A96B]/70 bg-black/40 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.28em] text-[#E7CF9A]" data-testid="featured-badge">
              <Star className="w-3 h-3 fill-current" /> As featured on LockedInForever
            </span>
          )}
          {w.banner_headline && <p className="text-xs md:text-sm uppercase tracking-[0.35em] text-white/90 mb-3 drop-shadow" style={{ ...bannerText(w.banner_style).headline, textShadow: "0 1px 3px rgba(0,0,0,.85), 0 0 18px rgba(0,0,0,.6)" }} data-testid="site-banner-headline">{w.banner_headline}</p>}
          <p className="font-script text-5xl md:text-7xl text-white drop-shadow mb-4" style={bannerText(w.banner_style).names} data-testid="site-banner-names">{w.couple_names}</p>
          {w.banner_subtitle && <p className="font-serif italic text-lg md:text-2xl text-white/95 max-w-2xl mb-4 drop-shadow" style={bannerText(w.banner_style).subtitle} data-testid="site-banner-subtitle">{w.banner_subtitle}</p>}
          {dateStr && <p className="text-sm md:text-base uppercase tracking-[0.3em]">{dateStr}</p>}
          {w.city && <p className="text-sm md:text-base mt-2 flex items-center gap-2"><MapPin className="w-4 h-4" /> {w.city}</p>}
          <div className="mt-12">
            {w.wedding_date && <CountdownCard names={w.couple_names} date={w.wedding_date} subtitle="Until our big day" />}
          </div>
          {w.rsvp_open && (
            <Link to={`/site/${w.slug}/upload`} className="absolute bottom-8 right-8 hidden md:flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm">
              <Camera className="w-4 h-4" /> Share photos
            </Link>
          )}
        </div>
      </section>

      <div id="videos"><VideoBox slug={w.slug} /></div>

      {/* Story */}
      {(w.story || w.proposal_story) && (
        <section className="section-pad">
          <div className="container-wide px-6 max-w-3xl text-center">
            <p className="font-script text-[var(--gold)] text-4xl mb-2">Our Story</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl mb-8">Two hearts, one love.</h2>
            {w.story && <p className="text-lg leading-relaxed text-[var(--muted)] mb-6 whitespace-pre-line">{w.story}</p>}
            {w.proposal_story && (
              <>
                <div className="gold-divider my-8" />
                <h3 className="font-serif text-2xl mb-4">The Proposal</h3>
                <p className="text-lg leading-relaxed text-[var(--muted)] whitespace-pre-line">{w.proposal_story}</p>
              </>
            )}
          </div>
        </section>
      )}

      {/* Details */}
      <section className="section-pad bg-[var(--bg-warm)]">
        <div className="container-wide px-6 grid md:grid-cols-3 gap-8 text-center">
          <VenueDetail w={w} slug={slug} onUnlocked={() => setUnlocked((n) => n + 1)} />
          {w.dress_code && (
            <div>
              <Heart className="w-6 h-6 text-[var(--gold)] mx-auto mb-3" />
              <h3 className="font-serif text-2xl mb-2">Dress Code</h3>
              <p className="text-[var(--muted)]">{w.dress_code}</p>
            </div>
          )}
          {dateStr && (
            <div>
              <Calendar className="w-6 h-6 text-[var(--gold)] mx-auto mb-3" />
              <h3 className="font-serif text-2xl mb-2">When</h3>
              <p className="text-[var(--muted)]">{dateStr}</p>
            </div>
          )}
        </div>
      </section>

      <GuestHub w={w} />

      {/* Registry */}
      <RegistrySection slug={w.slug} />

      {/* CTA / Upload memories */}
      <section className="bg-[var(--ink)] text-white py-20">
        <div className="container-wide px-6 text-center">
          <p className="font-script text-[var(--gold)] text-4xl mb-3">Share with us</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl mb-6">Got a photo to share?</h2>
          <Link to={`/site/${w.slug}/upload`} data-testid="public-upload-cta" className="btn-gold">Upload Your Memories</Link>
          {w.hashtag && <p className="font-script text-[var(--gold)] text-3xl mt-6">{w.hashtag}</p>}
        </div>
      </section>

      {/* Audio Blessings — guests record a voice note */}
      <BlessingsSection slug={w.slug} couple_names={w.couple_names} />
      <div className="text-center py-6"><Link to="/guest/login" className="text-xs uppercase tracking-[0.25em] text-[var(--gold)] underline underline-offset-4" data-testid="site-guest-login">Guest login</Link></div>

      <footer className="py-10 text-center text-xs text-[var(--muted)] uppercase tracking-[0.22em]">
        Built with love on <Link to="/" className="text-[var(--gold)]">LockedInForever</Link>
      </footer>
    </div>
  );
}


function BlessingsSection({ slug, couple_names }) {
  const [blessings, setBlessings] = useState([]);
  const [recording, setRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/blessings/${slug}`).then((r) => setBlessings(r.data || [])).catch(() => {});
  }, [slug]);

  async function startRecording() {
    if (!navigator.mediaDevices) { toast.error("Microphone not available"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const localChunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) localChunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(localChunks, { type: "audio/webm" });
        setPreview(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      setChunks(localChunks);
      mr.start();
      setRecorder(mr);
      setRecording(true);
      // Auto-stop after 60s
      setTimeout(() => { if (mr.state === "recording") mr.stop(); }, 60000);
    } catch (err) {
      toast.error("Mic permission denied");
    }
  }

  function stop() {
    if (recorder && recorder.state === "recording") recorder.stop();
    setRecording(false);
  }

  async function submit() {
    if (!preview || !name.trim()) { toast.error("Add your name and a recording"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("guest_name", name.trim());
      if (message) fd.append("message", message.trim());
      fd.append("audio", new File([preview], "blessing.webm", { type: "audio/webm" }));
      const r = await fetch(`${BACKEND_URL}/api/public/blessings/${slug}`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Submission failed");
      toast.success(`Thanks, ${name}! ${couple_names} will hear it soon.`);
      setPreview(null);
      setName("");
      setMessage("");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="py-20 bg-[var(--bg-warm)]" data-testid="blessings-section">
      <div className="container-wide px-6 max-w-3xl">
        <div className="text-center mb-10">
          <p className="font-script text-[var(--gold)] text-4xl mb-2">Bless us</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl">Leave a Voice Note</h2>
          <p className="text-[var(--muted)] mt-3">Tap record and share a wish. Up to 60 seconds.</p>
        </div>

        <div className="bg-white border border-[var(--line)] rounded-3xl p-6 md:p-8 shadow-sm">
          {!preview ? (
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={recording ? stop : startRecording}
                data-testid="record-blessing-btn"
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                  recording ? "bg-red-500 animate-pulse" : "bg-[var(--gold)] hover:scale-105"
                } text-white shadow-xl`}
              >
                {recording ? <Square className="w-7 h-7" /> : <Mic className="w-8 h-8" />}
              </button>
              <p className="text-sm text-[var(--muted)]">{recording ? "Recording… tap to stop" : "Tap to record"}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <audio controls src={URL.createObjectURL(preview)} className="w-full" data-testid="blessing-preview" />
              <input
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="blessing-name-input"
                className="input-luxe"
              />
              <textarea
                placeholder="Optional written note…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                data-testid="blessing-message-input"
                className="input-luxe resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="blessing-submit-btn"
                  className="btn-gold flex-1"
                >
                  {submitting ? "Sending…" : "Send blessing"}
                </button>
                <button
                  onClick={() => setPreview(null)}
                  className="btn-outline-gold"
                  data-testid="blessing-redo-btn"
                >
                  Redo
                </button>
              </div>
            </div>
          )}
        </div>

        {blessings.length > 0 && (
          <div className="mt-12">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] text-center mb-5">From the family</p>
            <div className="space-y-3">
              {blessings.slice(0, 5).map((b) => (
                <div key={b.blessing_id} className="bg-white border border-[var(--line)] rounded-2xl p-4 flex items-center gap-4" data-testid={`public-blessing-${b.blessing_id}`}>
                  <div className="w-9 h-9 rounded-full bg-[#C8A96B]/15 text-[var(--gold)] flex items-center justify-center text-sm font-medium shrink-0">
                    {b.guest_name[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{b.guest_name}</p>
                    {b.message && <p className="text-xs text-[var(--muted)] truncate">{b.message}</p>}
                  </div>
                  <audio controls src={`${BACKEND_URL}/api/files/${b.audio_path}`} className="h-8" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
